
import { type ToastActionElement, ToastProps } from "@/components/ui/toast"

import {
  useState,
  createContext,
  useContext,
  type Dispatch,
  type SetStateAction,
} from "react"

const TOAST_LIMIT = 5
const TOAST_REMOVE_DELAY = 1000000

export type ToasterToast = ToastProps & {
  id: string
  title?: React.ReactNode
  description?: React.ReactNode
  action?: ToastActionElement
}

const ToastContext = createContext<{
  toasts: ToasterToast[]
  addToast: (toast: ToasterToast) => void
  removeToast: (toastId: string) => void
  updateToast: (toast: ToasterToast) => void
}>(
  {
    toasts: [],
    addToast: () => {},
    removeToast: () => {},
    updateToast: () => {},
  }
)

export function ToastProvider({
  children,
}: {
  children: React.ReactNode
}): JSX.Element {
  const [toasts, setToasts] = useState<ToasterToast[]>([])

  const addToast = (toast: ToasterToast) => {
    setToasts((prevToasts) => {
      const nextToasts = [...prevToasts]

      // Find if we already added this toast
      const index = nextToasts.findIndex((t) => t.id === toast.id)

      if (index !== -1) {
        // Update the existing toast if it exists
        nextToasts[index] = { ...nextToasts[index], ...toast }
      } else {
        // If we're at the limit, remove the oldest one
        if (nextToasts.length >= TOAST_LIMIT) {
          nextToasts.shift()
        }

        // Then add the new one
        nextToasts.push({ ...toast })
      }

      return nextToasts
    })
  }

  const removeToast = (toastId: string) => {
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== toastId))
  }

  const updateToast = (toast: ToasterToast) => {
    setToasts((prevToasts) => {
      const nextToasts = [...prevToasts]
      const index = nextToasts.findIndex((t) => t.id === toast.id)

      if (index !== -1) {
        nextToasts[index] = { ...nextToasts[index], ...toast }
      }

      return nextToasts
    })
  }

  return (
    <ToastContext.Provider 
      value={{
        toasts,
        addToast,
        removeToast,
        updateToast,
      }}
    >
      {children}
    </ToastContext.Provider>
  )
}

export function useToast() {
  const { toasts, addToast, removeToast, updateToast } = useContext(ToastContext)

  function toast({
    variant = "default",
    title,
    description,
    action,
    ...props
  }: Omit<ToasterToast, "id">) {
    const id = Math.random().toString(36).substring(2, 9)

    addToast({
      id,
      variant,
      title,
      description,
      action,
      ...props,
    })

    return {
      id,
      dismiss: () => removeToast(id),
      update: (props: ToasterToast) =>
        updateToast({ ...props, id }),
    }
  }

  return {
    toast,
    toasts,
    dismiss: (toastId: string) => removeToast(toastId),
  }
}

// Create a standalone toast function for direct imports that doesn't depend on context
export const toast = (props: Omit<ToasterToast, "id">) => {
  // Generate a unique ID for this toast
  const id = Math.random().toString(36).substring(2, 9)
  
  console.warn(
    "Direct toast import is deprecated. Use the useToast hook inside a component for better functionality."
  )
  
  // Return an object with the same shape as the hook version
  return {
    id,
    // These are just stubs since we can't access the context
    dismiss: () => {},
    update: () => {},
  }
}
